import * as React from 'react';

import { ChevronRight } from 'lucide-react';
import {
  Breadcrumb as AriaBreadcrumb,
  BreadcrumbRenderProps,
  Breadcrumbs as AriaBreadcrumbs,
  Link as AriaLink,
  LinkRenderProps,
  composeRenderProps,
} from 'react-aria-components';

import { cn } from '../../../lib/stylingUtils';

/**
 * Breadcrumbs component for displaying navigation hierarchy.
 * Shows a trail of links leading to the current page location.
 *
 * @example
 * ```tsx
 * <Breadcrumbs>
 *   <Breadcrumb>
 *     <BreadcrumbLink href="/">Home</BreadcrumbLink>
 *   </Breadcrumb>
 *   <Breadcrumb>
 *     <BreadcrumbLink href="/files">Files</BreadcrumbLink>
 *   </Breadcrumb>
 *   <Breadcrumb>
 *     <BreadcrumbLink>Documents</BreadcrumbLink>
 *   </Breadcrumb>
 * </Breadcrumbs>
 * ```
 */
const Breadcrumbs = ({
  className,
  ...props
}: React.ComponentProps<typeof AriaBreadcrumbs>) => (
  <AriaBreadcrumbs
    className={cn(
      '@Breadcrumbs',
      'flex items-center space-x-1 text-sm text-foreground/20',
      className,
    )}
    {...props}
  />
);

/**
 * Individual breadcrumb item component.
 * Contains a link and separator (except for the last item).
 */
const Breadcrumb = ({
  className,
  ...props
}: React.ComponentProps<typeof AriaBreadcrumb>) => (
  <AriaBreadcrumb
    className={composeRenderProps(className, (className) =>
      cn(
        '@Breadcrumb',
        'flex items-center',
        /* Not last child - add separator */
        '[&:not(:last-child)]:after:ml-2 [&:not(:last-child)]:after:size-4 [&:not(:last-child)]:after:content-[""]',
        className,
      ),
    )}
    {...props}
  >
    {composeRenderProps(
      props.children,
      (
        children,
        renderProps: BreadcrumbRenderProps & {
          defaultChildren: React.ReactNode | undefined;
        },
      ) => (
        <>
          {children}
          {!renderProps.isCurrent && (
            <ChevronRight className="ml-2 size-4 text-foreground/20" />
          )}
        </>
      ),
    )}
  </AriaBreadcrumb>
);

/**
 * Breadcrumb link component.
 * Renders as a link for navigation items or text for the current page.
 */
const BreadcrumbLink = ({
  className,
  ...props
}: React.ComponentProps<typeof AriaLink>) => (
  <AriaLink
    className={composeRenderProps(
      cn('@BreadcrumbLink', className),
      (className, renderProps: LinkRenderProps) =>
        cn(
          'transition-colors',
          renderProps.isCurrent
            ? 'font-medium text-foreground'
            : 'text-foreground/20 hover:text-foreground',
          /* Disabled */
          'rac-disabled:pointer-events-none rac-disabled:opacity-50',
          /* Focus */
          'rac-focus-visible:ring-2 rac-focus-visible:ring-primary rac-focus-visible:ring-offset-2 rac-focus-visible:outline-none',
          className,
        ),
    )}
    {...props}
  />
);

export { Breadcrumbs, Breadcrumb, BreadcrumbLink };
